import React, { useEffect, useLayoutEffect, useState } from 'react';
import { compose } from 'redux';
import { string, func, bool, oneOf, oneOfType, shape } from 'prop-types';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { AspectRatioWrapper, MaterialSymbol, NamedLink, ResponsiveImage } from '../../components';
import SuperhostBadge from '../SuperhostBadge/SuperhostBadge';
import { isSuperhostActive } from '../../util/superhost';

// Utils
import { displayPrice } from '../../util/configHelpers';
import {
  applyHourlyCustomerPriceSubunits,
  getCustomerFacingPriceSubunitsAndCurrency,
  normalizeSearchCapacityGuestRange,
} from '../../util/cocopoolPricing';
import { formatMoneyCompact } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { isFirstBookingDiscountEligible } from '../../util/firstBookingDiscount';
import {
  getWeekdayBookingDiscountPercent,
  isWeekdaySearch,
} from '../../util/weekdayBookingDiscount';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { types as sdkTypes } from '../../util/sdkLoader';
import { propTypes } from '../../util/types';
import { isMobileViewport, lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug, parse } from '../../util/urlHelpers';

import { isBookingProcessAlias } from '../../transactions/transaction';
import IconCard from '../SavedCardDetails/IconCard/IconCard';

import css from './ListingCard.module.css';
import { createResourceLocatorString } from '../../util/routes';
import { appendPrefillQueryToPath } from '../../util/listingBookingPrefill';
import {
  applyFaceToListing,
  faceLinkQuery,
  listingFaceRequestFromLocation,
  resolveListingFace,
} from '../../util/listingFaces';
import Slider from 'react-slick';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { cardExtrasLine } from '../../util/listingCardExtras';
import {
  firstBookingDiscountedSubunits,
  weekdayDiscountedSubunits,
} from '../../util/listingCardPrice';
import {
  INDOOR_SPACE_PRICING_EXTRA,
  INDOOR_SPACE_QUERY_PARAM,
  featuredIndoorSpace,
} from '../../util/indoorSpaces';
import { isRainProofListing } from '../../util/searchListingTabs';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

// Proporción de la foto en la tarjeta, del diseño (más apaisada que la variante
// `listing-card` del config, que se recorta con object-fit: cover).
const CARD_IMAGE_ASPECT_WIDTH = 13;
const CARD_IMAGE_ASPECT_HEIGHT = 8;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoneyCompact(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/** 20 % dto. primera reserva (publicData.firstBookingDiscountEnabled). */
const buildFirstBookingDiscountDisplay = (price, currency, intl) => {
  const { Money } = sdkTypes;
  if (!price || price.currency !== currency) {
    return null;
  }
  try {
    // Misma aritmética que el filtro "Precio total" (util/listingCardPrice).
    const amount = firstBookingDiscountedSubunits(price.amount, price.currency);
    if (!amount) {
      return null;
    }
    const discountedMoney = new Money(amount, price.currency);
    const originalFormatted = formatMoneyCompact(intl, price);
    const discountedFormatted = formatMoneyCompact(intl, discountedMoney);
    return {
      discountedAmount: amount,
      originalFormatted,
      discountedFormatted,
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.firstBookingDiscountPriceTitle' },
        { original: originalFormatted, discounted: discountedFormatted }
      ),
    };
  } catch (e) {
    return null;
  }
};

/** Descuento primera reserva en card: mismo criterio que el precio en overlay inferior. */
const getListingCardDiscountDisplay = (price, publicData, author, config, intl) => {
  const foundListingTypeConfig = config.listing.listingTypes.find(
    c => c.listingType === publicData?.listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice) {
    return null;
  }
  if (!isBookingProcessAlias(publicData?.transactionProcessAlias)) {
    return null;
  }
  if (!isFirstBookingDiscountEligible(publicData, author)) {
    return null;
  }
  return buildFirstBookingDiscountDisplay(price, config.currency, intl);
};

/** Dto. entre semana (publicData.weekdayBookingDiscountPercent) en card. */
const buildWeekdayDiscountDisplay = (price, currency, intl, percent) => {
  const { Money } = sdkTypes;
  if (!price || price.currency !== currency || !percent) {
    return null;
  }
  try {
    // Misma aritmética que el filtro "Precio total" (util/listingCardPrice).
    const amount = weekdayDiscountedSubunits(price.amount, price.currency, percent);
    if (!amount) {
      return null;
    }
    const discountedMoney = new Money(amount, price.currency);
    const originalFormatted = formatMoneyCompact(intl, price);
    const discountedFormatted = formatMoneyCompact(intl, discountedMoney);
    return {
      discountedAmount: amount,
      originalFormatted,
      discountedFormatted,
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.weekdayDiscountPriceTitle' },
        { original: originalFormatted, discounted: discountedFormatted, percent }
      ),
    };
  } catch (e) {
    return null;
  }
};

/**
 * % de dto. entre semana anunciable en la card (0 si no aplica). No depende de las fechas
 * buscadas: el anfitrión lo ofrece igualmente, así que el badge se muestra siempre. La
 * preferencia frente al dto. de primera reserva se decide en el componente (primera reserva gana).
 */
const getListingCardWeekdayDiscountPercent = (publicData, config) => {
  const foundListingTypeConfig = config.listing.listingTypes.find(
    c => c.listingType === publicData?.listingType
  );
  if (!displayPrice(foundListingTypeConfig)) {
    return 0;
  }
  if (!isBookingProcessAlias(publicData?.transactionProcessAlias)) {
    return 0;
  }
  return getWeekdayBookingDiscountPercent(publicData);
};

/**
 * Solo un tag en la esquina de la card:
 * superhost > dto. primera reserva > dto. entre semana > resto (Nuevo, etc.).
 */
export const resolveListingCardTopTag = ({
  isSuperhost,
  hasFirstBookingDiscount,
  hasWeekdayDiscount,
  weekdayDiscountPercent,
  effectiveTag,
}) => {
  if (isSuperhost) {
    return { type: 'superhost' };
  }
  if (hasFirstBookingDiscount) {
    return { type: 'firstBookingDiscount' };
  }
  if (hasWeekdayDiscount) {
    return { type: 'weekdayDiscount', percent: weekdayDiscountPercent };
  }
  if (effectiveTag) {
    return { type: 'custom', tag: effectiveTag };
  }
  return null;
};

/**
 * Resuelve el tag "custom" (prioridad 3): un tag configurado en
 * `publicData.tag` tiene preferencia sobre el "Nuevo" automático. Un tag cuyo
 * texto sea 'Nuevo' se ignora (esa etiqueta la gestiona el camino automático).
 */
export const resolveEffectiveTag = ({ isNewPool, tag }) => {
  const configuredTag =
    Array.isArray(tag) && tag.length > 0 && tag[0]?.text !== 'Nuevo' ? tag[0] : null;
  if (configuredTag) {
    return configuredTag;
  }
  return isNewPool ? { text: 'Nuevo' } : null;
};

const customTagStyleClass = tagText => {
  const key = tagText?.trim().toLowerCase();
  if (key === 'nuevo') {
    return css.newTag;
  }
  if (key === 'destacado') {
    return css.featuredTag;
  }
  return null;
};

const ListingCardTopTag = ({ topTag, superhostLabel }) => {
  if (!topTag) {
    return null;
  }
  if (topTag.type === 'superhost') {
    return (
      <div className={classNames(css.ListingCard_tag, css.superhostTag)}>
        <span className={css.superhostTagStar} aria-hidden="true">
          ★
        </span>
        {superhostLabel}
      </div>
    );
  }
  if (topTag.type === 'firstBookingDiscount') {
    return (
      <div className={classNames(css.ListingCard_tag, css.firstBookingTag)}>
        <FormattedMessage id="ListingCard.firstBookingDiscountBadge" />
      </div>
    );
  }
  if (topTag.type === 'weekdayDiscount') {
    return (
      <div className={classNames(css.ListingCard_tag, css.weekdayDiscountTag)}>
        <FormattedMessage
          id="ListingCard.weekdayDiscountBadge"
          values={{ percent: topTag.percent }}
        />
      </div>
    );
  }
  return (
    <div
      className={classNames(css.ListingCard_tag, customTagStyleClass(topTag.tag?.text))}
      style={
        customTagStyleClass(topTag.tag?.text) ? undefined : { backgroundColor: topTag.tag?.colour }
      }
    >
      {topTag.tag?.text}
    </div>
  );
};

function SampleNextArrow(props) {
  const { className, style, onClick } = props;

  return (
    <div
      className={className}
      style={{ ...style, display: 'block', userSelect: 'none' }}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <svg width="30px" height="30px" viewBox="0 0 0.75 0.75" xmlns="http://www.w3.org/2000/svg">
        <path
          fill="#fff"
          fillRule="evenodd"
          d="m0.264 0.081 0.272 0.272a0.025 0.025 0 0 1 0.008 0.018 0.026 0.026 0 0 1 -0.008 0.019c-0.098 0.096 -0.193 0.188 -0.284 0.278 -0.005 0.004 -0.023 0.015 -0.038 -0.001 -0.014 -0.016 -0.006 -0.03 0 -0.036l0.265 -0.259 -0.253 -0.253c-0.009 -0.013 -0.008 -0.024 0.002 -0.035 0.011 -0.011 0.023 -0.011 0.036 -0.002Z"
        />
      </svg>
    </div>
  );
}

function SamplePrevArrow(props) {
  const { className, style, onClick } = props;

  return (
    <div
      className={className}
      style={{ ...style, display: 'block', userSelect: 'none' }}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <svg width="30px" height="30px" viewBox="0 0 0.75 0.75" xmlns="http://www.w3.org/2000/svg">
        <path
          fill="#fff"
          fillRule="evenodd"
          d="M0.271 0.371c0.086 -0.087 0.171 -0.171 0.253 -0.253a0.024 0.024 0 0 0 0 -0.034c-0.012 -0.013 -0.03 -0.012 -0.039 -0.003 -0.085 0.086 -0.175 0.175 -0.268 0.268 -0.007 0.006 -0.011 0.013 -0.011 0.022 0 0.008 0.004 0.016 0.011 0.022l0.281 0.274a0.028 0.028 0 0 0 0.039 -0.001c0.013 -0.013 0.008 -0.027 0.002 -0.033a127.79 127.79 0 0 1 -0.269 -0.262Z"
        />
      </svg>
    </div>
  );
}

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, discountDisplay, priceLabelUsesCapacityFilter } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  if (!isBookable) {
    return null;
  }
  const amount = discountDisplay ? (
    <>
      <span className={css.priceStruck}>{discountDisplay.originalFormatted}</span>{' '}
      <span className={css.priceDiscounted}>{discountDisplay.discountedFormatted}</span>
    </>
  ) : (
    formattedPrice
  );

  // "desde" en su línea y el precio debajo. Con filtro de capacidad no hay "desde": el precio
  // ya es el del tramo de ese grupo.
  return (
    <div className={css.price} title={discountDisplay ? discountDisplay.priceTitle : priceTitle}>
      {priceLabelUsesCapacityFilter ? null : (
        <div className={css.priceFrom}>
          <FormattedMessage id="ListingCard.priceFromWord" />
        </div>
      )}
      <div className={css.priceValue}>
        {amount}
        <span className={css.perHourSuffix}>
          <FormattedMessage id="ListingCard.perHour" />
        </span>
      </div>
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const routes = useRouteConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    currentUser,
    listingId,
    isAuthenticated,
    onupdateProfile,
    history,
    location: routeLocation,
    reviews,
    onFetchReviews,
    openListingInNewTab,
    searchCapacityGuestRange,
    faceParam,
  } = props;

  const [inProgress, setinProgress] = useState(false);

  const { Money } = sdkTypes;
  // La búsqueda decide qué cara (piscina / jardín) enseña la tarjeta: `espacio` y la
  // pestaña de jardines son peticiones explícitas; la fecha de `/s` no, para que un
  // diciembre no se convierta en "el usuario ha pedido piscina". Ver listingFaces.
  const baseListing = ensureListing(listing);
  // `faceParam` lo fija quien pinta la tarjeta fuera de la búsqueda (las landings SEO de
  // jardines), donde la URL no dice nada de la cara.
  const faceRequest = {
    ...listingFaceRequestFromLocation(routeLocation || {}),
    ...(faceParam ? { faceParam } : {}),
  };
  const cardFace = resolveListingFace({ listing: baseListing, ...faceRequest });
  const currentListing = applyFaceToListing(baseListing, cardFace);
  const isSuperhost = isSuperhostActive(currentListing.author);
  const classes = classNames(rootClassName || css.root, className);
  const id = currentListing.id.uuid;
  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author?.id?.uuid === currentUser?.id?.uuid;
  const { title = '', price = new Money(100, 'EUR'), publicData } = currentListing.attributes || {};
  const capacityForPricing = searchCapacityGuestRange;
  const customerFacing = getCustomerFacingPriceSubunitsAndCurrency(
    currentListing,
    capacityForPricing
  );
  const priceForDisplay = customerFacing
    ? new Money(customerFacing.amount, customerFacing.currency)
    : price;
  const { location: listingGeo, maxGuest, tag = [], city = '', province = '' } = publicData || {};

  const { favouriteListings = [] } = currentUser?.attributes?.profile?.privateData || {};
  const isFavourite = favouriteListings?.includes(listingId);
  const slug = createSlug(title);

  // Espacio interior destacado (el más barato, ver featuredIndoorSpace): etiqueta siempre y,
  // con el chip "Con espacio interior", sus fotos primero y el enlace con el espacio ya añadido
  // a la reserva. El precio de la tarjeta no cambia: sigue siendo el de por hora.
  const searchQuery = parse(routeLocation?.search || '');
  const isIndoorSearch = searchQuery.interior === true || searchQuery.interior === 'true';
  const featuredSpace = featuredIndoorSpace(baseListing);

  // Con el chip: la primera foto del espacio, luego las de la cara que enseña la tarjeta y al
  // final el resto de las del espacio (que pueden no estar en la cara: las subidas desde el
  // espacio se ocultan en las dos).
  const faceImages = currentListing.images || [];
  const spaceImages =
    isIndoorSearch && featuredSpace
      ? featuredSpace.imageIds
          .map(uuid => (baseListing.images || []).find(img => img?.id?.uuid === uuid))
          .filter(Boolean)
      : [];
  const spaceImageIds = new Set(spaceImages.map(img => img.id.uuid));
  const cardImages = spaceImages.length
    ? [
        spaceImages[0],
        ...faceImages.filter(img => !spaceImageIds.has(img?.id?.uuid)),
        ...spaceImages.slice(1),
      ]
    : faceImages;
  const firstImage = cardImages.length > 0 ? cardImages[0] : null;
  const listingLocation = listingGeo && listingGeo?.address && listingGeo?.address?.split(', ')[1];

  // Ratings from author's public profile data
  const authorPublicData = currentListing?.author?.attributes?.profile?.publicData || {};
  const avgRating =
    typeof authorPublicData.averageRating === 'number'
      ? authorPublicData.averageRating
      : Number(authorPublicData.averageRating);
  const totalReviews =
    typeof authorPublicData.totalReviews === 'number'
      ? authorPublicData.totalReviews
      : Number(authorPublicData.totalReviews);

  const THREE_MONTHS_MS = 3 * 30 * 24 * 60 * 60 * 1000;
  const listingCreatedAt = currentListing.attributes?.createdAt;
  const isNewPool =
    listingCreatedAt instanceof Date &&
    Date.now() - listingCreatedAt.getTime() < THREE_MONTHS_MS &&
    !(totalReviews > 0);

  const { variantPrefix = 'listing-card' } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const settings = {
    speed: 500,
    dots: false,
    arrows: true,
    slidesToShow: 1,
    infinite: true,
    slidesToScroll: 1,
    // Sin esto el carrusel monta TODAS las fotos de cada anuncio aunque solo se vea
    // una, y `loading="lazy"` no las frena porque las diapositivas ocultas están al
    // lado de la visible: una búsqueda descargaba ~510 imágenes para enseñar 24.
    // Además, cada foto distinta que se sirve cuesta 340 créditos al mes.
    // Ver docs/sharetribe-credits.md.
    lazyLoad: 'ondemand',
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
  };

  const handleShareAddtoFavourite = async (listingId, favourites) => {
    if (isAuthenticated) {
      setinProgress(true);
      const newArr = [...favourites];
      if (isFavourite) {
        const indexOfId = newArr?.indexOf(listingId);
        newArr.splice(indexOfId, 1);
      } else {
        newArr.push(listingId);
      }
      const profile = {
        privateData: {
          favouriteListings: newArr,
        },
      };
      await onupdateProfile(profile).then(() => {
        setinProgress(false);
      });
    } else {
      history.push('/login');
    }
  };

  const handleRedirectLink = () => {
    // La cara solo se fija en el enlace si la búsqueda la pidió (pestaña de jardines o
    // `?espacio=`); si la decidió la fecha, la ficha la vuelve a decidir con esa misma
    // fecha, que viaja en el prefill. Ver faceLinkQuery.
    const indoorSpaceQuery =
      isIndoorSearch && featuredSpace?.pricing === INDOOR_SPACE_PRICING_EXTRA
        ? { [INDOOR_SPACE_QUERY_PARAM]: featuredSpace.id }
        : {};
    const path = createResourceLocatorString(
      'ListingPage',
      routes,
      { id, slug },
      { ...faceLinkQuery(cardFace, faceRequest), ...indoorSpaceQuery }
    );
    const pathWithPrefill = appendPrefillQueryToPath(path, routeLocation?.search || '');
    // On phones a new tab is a dead end (no visible way back to the results), so the
    // listing opens in the same tab there even when a new tab was asked for.
    if (openListingInNewTab && typeof window !== 'undefined' && !isMobileViewport()) {
      const url = pathWithPrefill.startsWith('http')
        ? pathWithPrefill
        : `${window.location.origin}${pathWithPrefill}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      history.push(pathWithPrefill);
    }
  };

  const firstBookingDiscountDisplay = getListingCardDiscountDisplay(
    priceForDisplay,
    publicData,
    currentListing.author,
    config,
    intl
  );
  // El badge de dto. entre semana se muestra siempre que el anfitrión lo ofrezca (y no haya
  // dto. de primera reserva, que tiene preferencia en la card). El precio tachado, en cambio,
  // solo cuando la búsqueda es de un día L–V: en fin de semana el dto. existe pero no se aplica.
  const weekdayDiscountPercent = firstBookingDiscountDisplay
    ? 0
    : getListingCardWeekdayDiscountPercent(publicData, config);
  const weekdayDiscountDisplay =
    weekdayDiscountPercent && isWeekdaySearch(routeLocation?.search)
      ? buildWeekdayDiscountDisplay(priceForDisplay, config.currency, intl, weekdayDiscountPercent)
      : null;
  const discountDisplay = firstBookingDiscountDisplay || weekdayDiscountDisplay;
  const priceLabelUsesCapacityFilter =
    normalizeSearchCapacityGuestRange(searchCapacityGuestRange) != null;
  // "+240 €": el precio del extra con la comisión de cliente, como en la ficha.
  const indoorBadgePrice =
    featuredSpace?.pricing === INDOOR_SPACE_PRICING_EXTRA
      ? formatMoneyCompact(
          intl,
          new Money(
            publicData?.unitType === 'hour'
              ? applyHourlyCustomerPriceSubunits(featuredSpace.price.amount)
              : featuredSpace.price.amount,
            featuredSpace.price.currency
          )
        )
      : null;
  const effectiveTag = resolveEffectiveTag({ isNewPool, tag });
  const superhostLabel = intl.formatMessage({ id: 'ListingCard.superhostBadge' });
  const topTag = resolveListingCardTopTag({
    isSuperhost,
    hasFirstBookingDiscount: Boolean(firstBookingDiscountDisplay),
    hasWeekdayDiscount: weekdayDiscountPercent > 0,
    weekdayDiscountPercent,
    effectiveTag,
  });
  const showTopRightOverlay = Boolean(topTag);

  // Cuerpo de la tarjeta: valoración junto al título; debajo, municipio y provincia (+ capacidad);
  // equipamiento, y el distintivo de lluvia (misma regla que el chip de la búsqueda).
  const hasRating = !isNaN(avgRating) && avgRating > 0 && totalReviews > 0;
  const extrasField = config.listing?.listingFields?.find(f => f.key === 'extras');
  const extrasLine = cardExtrasLine(publicData?.extras, extrasField);
  const capacityLabel =
    maxGuest === '51_above'
      ? intl.formatMessage({ id: 'ListingCard.capacityAbove' })
      : maxGuest
      ? intl.formatMessage({ id: 'ListingCard.capacityUpTo' }, { count: maxGuest })
      : null;
  // "Valdemorillo, Madrid". La provincia solo está en parte de los anuncios, y no se repite
  // cuando coincide con el municipio ("Madrid, Madrid").
  const place = [city, province && province !== city ? province : null].filter(Boolean).join(', ');
  const locationLine = [place, capacityLabel].filter(Boolean).join(' · ');
  const isRainProof = isRainProofListing(currentListing);
  // Para cuántas personas, si el anfitrión lo declaró (los aptos por "Zona techada" o por ser
  // interiores no lo tienen).
  const rainCapacity = publicData?.weatherProtection?.rain?.capacity || null;

  return (
    <div onClick={() => handleRedirectLink()} className={classes}>
      {cardImages.length > 1 ? (
        <div className={css.sliderWrapper}>
          <Slider {...settings}>
            {cardImages.map((st, index) => {
              return (
                <AspectRatioWrapper
                  width={CARD_IMAGE_ASPECT_WIDTH}
                  height={CARD_IMAGE_ASPECT_HEIGHT}
                  key={index}
                  className={css.aspectRatioWrapper}
                >
                  <ResponsiveImage
                    rootClassName={css.rootForImage}
                    alt={title}
                    image={st}
                    variants={Object.keys(st?.attributes?.variants).filter(k =>
                      k.startsWith(variantPrefix)
                    )}
                    sizes={renderSizes}
                    // `eager` a propósito: quién decide cuántas fotos existen es el
                    // `lazyLoad` del carrusel, que solo monta la visible y sus vecinas.
                    // Con `lazy`, la foto que el carrusel monta al pasar de diapositiva
                    // no se llega a pedir nunca (nace ya desplazada) y se queda en blanco.
                    loading="eager"
                  />
                </AspectRatioWrapper>
              );
            })}
          </Slider>
          {/* Overlay de la etiqueta a nivel de card, fuera del Slider, para que no se
              arrastre con la foto al cambiar de imagen. */}
          <div className={css.priceBox}>
            {showTopRightOverlay ? (
              <div className={css.topRightOverlay}>
                <ListingCardTopTag topTag={topTag} superhostLabel={superhostLabel} />
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <AspectRatioWrapper
          width={CARD_IMAGE_ASPECT_WIDTH}
          height={CARD_IMAGE_ASPECT_HEIGHT}
          className={css.aspectRatioWrapperSingle}
        >
          <ResponsiveImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
          <div className={css.priceBox}>
            {showTopRightOverlay ? (
              <div className={css.topRightOverlay}>
                <ListingCardTopTag topTag={topTag} superhostLabel={superhostLabel} />
              </div>
            ) : null}
          </div>
        </AspectRatioWrapper>
      )}
      <div className={css.info}>
        {isSuperhost ? (
          <SuperhostBadge
            className={css.superhostBadge}
            user={currentListing.author}
            label={superhostLabel}
          />
        ) : null}
        <div className={classNames(css.titleRow, { [css.superhostTextOffset]: isSuperhost })}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {hasRating ? (
            <span className={css.rating}>
              <span className={css.ratingStar}>
                <IconCard brand="cardstar" />
              </span>
              {/* "5" y no "5,0", como el diseño; "4,8" si tiene decimal. */}
              {intl.formatNumber(Number(avgRating), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
              })}
              <span className={css.ratingCount}>({totalReviews})</span>
            </span>
          ) : null}
        </div>
        {locationLine ? <div className={css.detailLine}>{locationLine}</div> : null}
        {extrasLine ? <div className={css.detailLine}>{extrasLine}</div> : null}
        {isRainProof || featuredSpace ? (
          <div className={css.badges}>
            {isRainProof ? (
              <div className={css.rainBadge}>
                <MaterialSymbol name="beach_access" size={18} />
                {rainCapacity ? (
                  <FormattedMessage
                    id="ListingCard.rainProofFor"
                    values={{ count: rainCapacity }}
                  />
                ) : (
                  <FormattedMessage id="QuickFilters.lluvia" />
                )}
              </div>
            ) : null}
            {featuredSpace ? (
              <div className={css.rainBadge}>
                <MaterialSymbol name="weekend" size={18} />
                {indoorBadgePrice ? (
                  <FormattedMessage
                    id="ListingCard.indoorSpaceExtra"
                    values={{ price: indoorBadgePrice }}
                  />
                ) : (
                  <FormattedMessage id="ListingCard.indoorSpaceIncluded" />
                )}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className={css.cardBottonBox}>
          <PriceMaybe
            price={priceForDisplay}
            publicData={publicData}
            config={config}
            intl={intl}
            discountDisplay={discountDisplay}
            priceLabelUsesCapacityFilter={priceLabelUsesCapacityFilter}
          />
          <div
            className={css.favIcon}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              !inProgress && handleShareAddtoFavourite(listingId, favouriteListings);
            }}
          >
            {!isOwnListing ? (
              <div className={css.keepButton}>
                {isFavourite ? (
                  <div>
                    <span>
                      <IconCard brand="like" />
                    </span>
                  </div>
                ) : (
                  <div>
                    <span className={css.dislike}>
                      <IconCard brand="dislike" />
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {/* </SnackbarProvider> */}
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  openListingInNewTab: false,
  searchCapacityGuestRange: null,
  faceParam: null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
  // Desktop/tablet only: on a phone-sized viewport the listing always opens in the same tab.
  openListingInNewTab: bool,
  searchCapacityGuestRange: oneOfType([string, oneOf([null])]),
  // Valor de `?espacio=` que manda sobre la URL (p. ej. 'jardin').
  faceParam: string,
  location: shape({
    search: string,
  }),
};

export default compose(injectIntl, withRouter)(ListingCardComponent);
